import {Component, computed, inject, Signal, signal, ViewChild} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {APP_SIGNALS} from '@app/core/tokens/app-signals.token';
import {RESOURCE_SORT_OPTIONS, SortOption} from '@app/search/search-bar/search-bar.config';
import {SearchResultsComponent} from '@app/search/search-results/search-results.component';
import {InfiniteScrollDirective} from '@app/shared/directives/infinite-scroll.directive';
import {IndexService, InstanceUserRoleID, SearchResourcesInIndexParams} from '@middleware';
import {UserService} from '@services/user/user.service';
import {
  SectionHeaderAction,
  SectionHeaderActions,
} from '@shared/components/section-header-actions/section-header-actions';
import {TileInfo} from '@shared/types/tile-info';
import {saveAs} from 'file-saver';
import {lastValueFrom} from 'rxjs';
import {SidebarComponent} from '../navigation/sidebar/sidebar.component';
import {FILTERS_ME_TOKEN, FILTERS_TOKEN} from '../search/filters/filter';
import {RESOURCE_FILTERS, ResourceMeComponent} from '../search/filters/resources';
import {SearchBarComponent} from '../search/search-bar/search-bar.component';

@Component({
  standalone: true,
  selector: 'app-resource-explorer',
  imports: [SidebarComponent, SearchBarComponent, ReactiveFormsModule, SearchResultsComponent],
  providers: [
    {provide: FILTERS_TOKEN, useValue: RESOURCE_FILTERS},
    {provide: FILTERS_ME_TOKEN, useValue: ResourceMeComponent},
  ],
  templateUrl: './resource-explorer.component.html',
})
export class ResourceExplorerComponent implements SectionHeaderActions {
  @ViewChild(SearchBarComponent) searchBar!: SearchBarComponent;
  @ViewChild(InfiniteScrollDirective) infiniteScrollDirective?: InfiniteScrollDirective;

  private readonly appSignals = inject(APP_SIGNALS);

  tiles = signal<TileInfo[] | undefined>(undefined);
  query = computed(() => this.searchBar?.query());
  loading = signal(false);
  sort: SortOption[] = RESOURCE_SORT_OPTIONS;

  showAdd: Signal<boolean> = computed(() => this.shouldShowAddButton());
  showExport: Signal<boolean> = computed(() => this.userIsAdmin());
  readonly exporting = signal(false);

  constructor(
    private readonly userService: UserService,
    private readonly indexService: IndexService,
  ) {}

  async onInfiniteScroll(infiniteScroll: InfiniteScrollDirective) {
    await this.searchBar.loadNextPage();
    infiniteScroll.markLoadComplete();
  }

  public sectionHeaderActions: Signal<SectionHeaderAction[]> = computed(() => {
    const actions: SectionHeaderAction[] = [
      {
        target: '/help/resource-explorer/overview',
        targetTab: '_blank',
        caption: 'Help',
        color: 'var(--mat-sys-primary)',
        icon: 'help_outline',
      },
    ];

    // Only show the add resource button if logged in (any non-guest user)
    if (this.showAdd()) {
      actions.unshift({
        target: '/resource-explorer/add',
        caption: 'Add Resource',
        color: 'var(--mat-sys-primary)',
        icon: 'add_circle_outline',
      });
    }

    // Only show the Export to CSV button for (Resource Explorer) admins
    if (this.showExport()) {
      actions.splice(1, 0, {
        target: 'exportToCSV',
        caption: 'Export to CSV',
        color: 'var(--mat-sys-primary)',
        icon: 'file_download',
        loading: this.exporting(),
        onClick: () => this.downloadCSV(),
      });
    }

    return actions;
  });

  /**
   * Returns true if the current user is logged in (not a guest)
   */
  shouldShowAddButton(): boolean {
    return this.userIsLoggedIn();
  }

  async downloadCSV() {
    this.exporting.set(true);
    try {
      const config = this.searchBar.getCurrentSearchConfig();

      const params: SearchResourcesInIndexParams = {
        ...config.params,
        csv_export: true,
      };

      delete params.page;
      delete params.per_page;

      const csvResponse = await lastValueFrom(
        this.indexService.searchResourcesInIndex(params, config.body, 'text/csv'),
      );

      const csvBlob = new Blob([csvResponse], {type: 'text/csv'});

      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');

      const dateString = `${year}-${month}-${day}`;

      const instanceName = this.appSignals.instanceName();

      saveAs(csvBlob, `${instanceName}-resources-${dateString}.csv`);
    } finally {
      this.exporting.set(false);
    }
  }

  userIsAdmin(): boolean {
    return this.userService.userSignal()?.instance_user_role_id === InstanceUserRoleID.ADMIN;
  }

  userIsLoggedIn(): boolean {
    const isNormalUser = this.userService.userSignal()?.instance_user_role_id === InstanceUserRoleID.USER;
    return isNormalUser || this.userIsAdmin();
  }
}
